// ANGEL FLIGHT — HudOverlay: the gunner's combat HUD (flat DOM, composited LAST).
//
// Drawn as DOM over the canvas so the readouts stay crisp and are NOT
// grain/scanline-graded. REDESIGNED from scattered corner text into three
// GROUPED, LABELED modules (the playtest read the old corner lines as noise —
// "I don't know what SCRIP 3 means"):
//   • top-left   STATUS  — hull BAR + weapon·ammo + flares,
//   • top-centre OBJECTIVE — what you're doing (label) + timer + progress bar,
//   • top-right  PAYOUT  — SCRIP/SALVAGE with a "what this is" sublabel,
//     "+N" payout popups on every kill, and the combo meter beneath it.
// Plus the tracking reticle, the centre banner, the comms ticker, hitmarkers,
// and the damage vignette. No information shown twice.

import { BALANCE } from '../config/balance';
import { COMMS } from '../config/comms';
import type { TypedEmitter } from '../core/EventBus';
import type { Vec2, WeaponId } from '../core/types';

const HUD_GREEN = '#7dff9a'; // tactical readouts
const HUD_WARN = '#ff7a4d';
const HUD_RETICLE = '#ffd27a'; // warm amber gun-sight (also the payout accent)
const HUD_DIM = 'rgba(125,255,154,0.55)'; // secondary labels
const PANEL_BG = 'rgba(5,11,7,0.55)';
const PANEL_BORDER = '1px solid rgba(125,255,154,0.25)';

// Calmer, cleaner type (owner brief): a system SANS for readouts instead of the
// old wide-tracked monospace, with tabular figures so the changing numbers
// don't jitter. FONT_MONO stays available if a strict mono column is ever wanted.
const FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/** Short chrome code per weapon (matches the touch rack vocabulary). */
const WEAPON_CODES: Readonly<Record<WeaponId, string>> = Object.freeze({
  minigun: 'M134',
  autocannon: '30MM',
  railgun: 'RAIL',
  flamer: 'INCN',
});

/** Hitmarker pool size (concurrent flashes at high fire-rate). */
const HITMARKER_POOL = 6;

/** Payout popup pool size (concurrent "+N" floats at high kill-rate). */
const POPUP_POOL = 5;

/** Key hint shown only where a keyboard exists (touch devices use the FLARE
 *  button instead — a dead "[F]" hint there is just noise). */
const FLARE_HINT =
  'ontouchstart' in window || navigator.maxTouchPoints > 0 ? '' : ' [F]';

export class HudOverlay {
  private readonly root: HTMLDivElement;
  private readonly reticle: HTMLDivElement;

  // ── STATUS module (top-left): hull bar + weapon/ammo + flares ─────────────
  private readonly hullText: HTMLDivElement;
  private readonly hullFill: HTMLDivElement;
  private readonly ammo: HTMLDivElement;
  private readonly flares: HTMLDivElement;
  /** ms left on the flare cooldown (decremented per frame in update()). */
  private flareCdLeftMs = 0;
  /** Live weapon code shown inline on the ammo line (one line, not two). */
  private weaponCode = 'M134';
  private lastAmmo: { ammo: number; cap: number } | null = null;

  // ── OBJECTIVE module (top-centre): label + timer + progress bar ───────────
  private readonly objLabel: HTMLDivElement;
  private readonly timer: HTMLDivElement;
  private readonly objFill: HTMLDivElement;
  private objectiveTitle = 'HOLD THE LINE';
  private objectiveDurationMs = 0;
  /** ENDLESS: the objective timer COUNTS UP (time survived) and the progress bar
   *  + final-stretch urgent flip are disabled (there is no deadline). */
  private objectiveCountUp = false;
  /** Arm the one-shot objective callout fired on the next show(). */
  private pendingObjBanner = false;

  // ── PAYOUT module (top-right): SCRIP/SALV + sublabel + popups + combo ─────
  private readonly scripVal: HTMLDivElement;
  private readonly salvRow: HTMLDivElement;
  private readonly salvVal: HTMLDivElement;
  private scripTotal = 0;
  private salvageTotal = 0;
  private readonly popups: HTMLDivElement[] = [];
  private popupIdx = 0;
  private readonly combo: HTMLDivElement;

  private readonly hurt: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  private readonly comms: HTMLDivElement;
  private commsTimeout = 0;
  private readonly hitmarkers: HTMLDivElement[] = [];
  private hitmarkerIdx = 0;
  private readonly parent: HTMLElement;
  private bus: TypedEmitter | null = null;
  private maxHull = 100;
  /** View-space (1280×720) width/height the aim point is expressed in. */
  private readonly viewW = BALANCE.VIEW.width;
  private readonly viewH = BALANCE.VIEW.height;
  private fireBloom = 0;
  // Banner arbitration: a higher-priority callout (CHECK FIRE) can't be stomped
  // by a routine one (wave beat / streak) while it holds.
  private bannerTimeout = 0;
  private bannerPrio = 0;
  private bannerHoldUntil = 0;

  constructor(parent: HTMLElement) {
    this.parent = parent;
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      font: `600 14px/1.35 ${FONT}`,
      letterSpacing: '0.04em',
      fontVariantNumeric: 'tabular-nums',
      color: HUD_GREEN,
      textShadow: '0 0 6px rgba(0,0,0,0.9)',
      padding: 'max(env(safe-area-inset-top), 18px) max(env(safe-area-inset-right), 22px) max(env(safe-area-inset-bottom), 18px) max(env(safe-area-inset-left), 22px)',
      boxSizing: 'border-box',
    } as CSSStyleDeclaration);

    // Free-tracking reticle (crosshair + ranging ticks). Positioned every frame
    // from gunner.aim via update() — it FOLLOWS the finger and marks exactly
    // where shots are raycast. Starts centred until the first update.
    this.reticle = document.createElement('div');
    this.reticle.innerHTML = `
      <svg width="72" height="72" viewBox="0 0 72 72" fill="none" stroke="${HUD_RETICLE}" stroke-width="1.8">
        <circle cx="36" cy="36" r="20" opacity="0.95" />
        <circle cx="36" cy="36" r="30" opacity="0.35" />
        <path d="M36 0 v14 M36 58 v14 M0 36 h14 M58 36 h14" opacity="0.95" />
        <circle cx="36" cy="36" r="2.2" fill="${HUD_RETICLE}" stroke="none" />
      </svg>`;
    Object.assign(this.reticle.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      width: '72px',
      height: '72px',
      marginLeft: '-36px',
      marginTop: '-36px',
      transform: 'translate(50%, 50%)', // overwritten by update()
      willChange: 'transform',
      filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.8))',
    } as CSSStyleDeclaration);

    // Shared safe-area anchors (absolute children ignore the root's padding).
    const safeTop = 'max(env(safe-area-inset-top), 14px)';
    const safeLeft = 'max(env(safe-area-inset-left), 16px)';
    const safeRight = 'max(env(safe-area-inset-right), 16px)';

    // ── STATUS panel (top-left): hull bar, weapon·ammo, flares ──────────────
    const statusPanel = this.mkPanel({ top: safeTop, left: safeLeft });
    const hullRow = this.mkRow();
    hullRow.append(this.mkLabel('HULL'));
    const hullBar = document.createElement('div');
    Object.assign(hullBar.style, {
      width: '120px',
      height: '8px',
      border: '1px solid rgba(125,255,154,0.4)',
      borderRadius: '4px',
      overflow: 'hidden',
      background: 'rgba(0,0,0,0.4)',
    } as CSSStyleDeclaration);
    this.hullFill = document.createElement('div');
    Object.assign(this.hullFill.style, {
      width: '100%',
      height: '100%',
      background: HUD_GREEN,
      transition: 'width 160ms ease-out, background 160ms',
    } as CSSStyleDeclaration);
    hullBar.appendChild(this.hullFill);
    this.hullText = document.createElement('div');
    hullRow.append(hullBar, this.hullText);
    this.ammo = this.mkRow();
    this.flares = this.mkRow();
    this.flares.textContent = `FLARES READY${FLARE_HINT}`;
    statusPanel.append(hullRow, this.ammo, this.flares);

    // ── OBJECTIVE panel (top-centre): what you're doing + how long is left ──
    const objPanel = this.mkPanel({ top: safeTop, left: '50%' });
    objPanel.style.transform = 'translateX(-50%)';
    objPanel.style.textAlign = 'center';
    objPanel.style.alignItems = 'center';
    this.objLabel = this.mkLabel(this.objectiveTitle);
    this.timer = document.createElement('div');
    Object.assign(this.timer.style, {
      fontSize: '24px',
      fontWeight: '700',
      lineHeight: '1.1',
    } as CSSStyleDeclaration);
    const objBar = document.createElement('div');
    Object.assign(objBar.style, {
      width: '180px',
      height: '5px',
      border: '1px solid rgba(125,255,154,0.4)',
      borderRadius: '3px',
      overflow: 'hidden',
      background: 'rgba(0,0,0,0.4)',
    } as CSSStyleDeclaration);
    this.objFill = document.createElement('div');
    Object.assign(this.objFill.style, {
      width: '0%',
      height: '100%',
      background: HUD_GREEN,
      transition: 'width 300ms linear, background 300ms',
    } as CSSStyleDeclaration);
    objBar.appendChild(this.objFill);
    objPanel.append(this.objLabel, this.timer, objBar);

    // ── PAYOUT panel (top-right): the economy, finally labeled ──────────────
    const payPanel = this.mkPanel({ top: safeTop, right: safeRight });
    payPanel.style.alignItems = 'flex-end';
    payPanel.style.textAlign = 'right';
    const scripRow = this.mkRow();
    scripRow.append(this.mkLabel('SCRIP'));
    this.scripVal = document.createElement('div');
    Object.assign(this.scripVal.style, {
      fontSize: '18px',
      fontWeight: '700',
      color: HUD_RETICLE,
      minWidth: '56px',
      textAlign: 'right',
    } as CSSStyleDeclaration);
    scripRow.append(this.scripVal);
    this.salvRow = this.mkRow();
    this.salvRow.append(this.mkLabel('SALVAGE'));
    this.salvVal = document.createElement('div');
    Object.assign(this.salvVal.style, {
      fontWeight: '700',
      minWidth: '56px',
      textAlign: 'right',
    } as CSSStyleDeclaration);
    this.salvRow.append(this.salvVal);
    this.salvRow.style.display = 'none'; // appears with the first elite drop
    const paySub = this.mkLabel('EACH KILL PAYS · KEPT WIN OR LOSE');
    paySub.style.fontSize = '9px';
    paySub.style.opacity = '0.7';
    payPanel.append(scripRow, this.salvRow, paySub);

    // Payout popup pool: "+N" floats rising off the payout panel on each kill.
    for (let i = 0; i < POPUP_POOL; i++) {
      const p = document.createElement('div');
      Object.assign(p.style, {
        position: 'absolute',
        top: '64px',
        right: '12px',
        fontSize: '16px',
        fontWeight: '700',
        color: HUD_RETICLE,
        opacity: '0',
        willChange: 'transform, opacity',
        pointerEvents: 'none',
      } as CSSStyleDeclaration);
      this.popups.push(p);
      payPanel.appendChild(p);
    }

    // Combo meter: rides directly under the payout panel — it's a PAY
    // multiplier, so it lives with the money.
    this.combo = document.createElement('div');
    Object.assign(this.combo.style, {
      position: 'absolute',
      top: `calc(${safeTop} + 104px)`,
      right: safeRight,
      textAlign: 'right',
      fontSize: '20px',
      fontWeight: '700',
      color: HUD_WARN,
      transformOrigin: 'right center',
    } as CSSStyleDeclaration);

    // Full-screen red damage vignette (flashes when the gunner takes hull damage).
    this.hurt = document.createElement('div');
    Object.assign(this.hurt.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      boxShadow: 'inset 0 0 160px 40px rgba(255,30,20,0.0)',
      transition: 'box-shadow 90ms ease-out',
    } as CSSStyleDeclaration);

    // Crew comms ticker (bottom-centre): one radio line at a time from the
    // CrewComms director — the genre's feel carrier.
    this.comms = document.createElement('div');
    Object.assign(this.comms.style, {
      position: 'absolute',
      left: '50%',
      bottom: 'max(env(safe-area-inset-bottom), 52px)',
      transform: 'translateX(-50%)',
      maxWidth: '70%',
      textAlign: 'center',
      font: `600 15px/1.4 ${FONT}`,
      letterSpacing: '0.04em',
      opacity: '0',
      transition: 'opacity 250ms',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as CSSStyleDeclaration);

    this.banner = document.createElement('div');
    Object.assign(this.banner.style, {
      position: 'absolute',
      left: '50%',
      top: '40%',
      transform: 'translate(-50%, -50%)',
      font: `700 clamp(26px, 5vw, 38px)/1.05 ${FONT}`,
      letterSpacing: '0.08em',
      opacity: '0',
      transition: 'opacity 200ms',
      textAlign: 'center',
    } as CSSStyleDeclaration);

    // Pooled hitmarkers: four diagonal ticks that flash at the impact point
    // (white = hit, amber = kill; headshots punch bigger).
    for (let i = 0; i < HITMARKER_POOL; i++) {
      const m = document.createElement('div');
      m.innerHTML = `
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
          <path d="M9 9 l6 6 M27 9 l-6 6 M9 27 l6 -6 M27 27 l-6 -6" />
        </svg>`;
      Object.assign(m.style, {
        position: 'absolute',
        left: '0',
        top: '0',
        width: '36px',
        height: '36px',
        marginLeft: '-18px',
        marginTop: '-18px',
        opacity: '0',
        color: '#ffffff',
        willChange: 'transform, opacity',
        filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9))',
      } as CSSStyleDeclaration);
      this.hitmarkers.push(m);
      this.root.appendChild(m);
    }

    this.root.append(
      this.hurt,
      this.reticle,
      statusPanel,
      objPanel,
      payPanel,
      this.combo,
      this.comms,
      this.banner,
    );
    parent.appendChild(this.root);

    this.setHull(this.maxHull, this.maxHull);
    this.paintAmmo();
    this.paintCurrency();
  }

  // ── tiny DOM kit (panels/rows/labels share one look) ─────────────────────

  /** A grouped HUD module: translucent panel, hairline border, stacked rows. */
  private mkPanel(pos: Partial<CSSStyleDeclaration>): HTMLDivElement {
    const d = document.createElement('div');
    Object.assign(d.style, {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      padding: '8px 12px',
      background: PANEL_BG,
      border: PANEL_BORDER,
      borderRadius: '10px',
      ...pos,
    } as CSSStyleDeclaration);
    return d;
  }

  /** One horizontal row inside a panel. */
  private mkRow(): HTMLDivElement {
    const d = document.createElement('div');
    Object.assign(d.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      whiteSpace: 'nowrap',
    } as CSSStyleDeclaration);
    return d;
  }

  /** A small, dim, uppercase module label — the "what is this" carrier. */
  private mkLabel(text: string): HTMLDivElement {
    const d = document.createElement('div');
    d.textContent = text;
    Object.assign(d.style, {
      fontSize: '10px',
      fontWeight: '700',
      letterSpacing: '0.1em',
      color: HUD_DIM,
    } as CSSStyleDeclaration);
    return d;
  }

  /** One status line for the gun: `M134 · 480/480` (code tracks the live gun). */
  private paintAmmo(): void {
    const a = this.lastAmmo;
    this.ammo.textContent = a
      ? `${this.weaponCode} · ${Math.ceil(a.ammo)}/${a.cap}`
      : `${this.weaponCode} · —`;
  }

  private paintCurrency(): void {
    this.scripVal.textContent = String(this.scripTotal);
    this.salvVal.textContent = String(this.salvageTotal);
    this.salvRow.style.display = this.salvageTotal > 0 ? 'flex' : 'none';
  }

  /** Float a "+N" / "−N" payout popup off the payout panel (pooled). */
  private spawnPopup(delta: number): void {
    const p = this.popups[this.popupIdx];
    this.popupIdx = (this.popupIdx + 1) % POPUP_POOL;
    if (!p) return;
    p.textContent = delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
    p.style.color = delta > 0 ? HUD_RETICLE : HUD_WARN;
    p.style.transition = 'none';
    p.style.opacity = '1';
    p.style.transform = 'translateY(0)';
    void p.offsetWidth; // reflow, then ease out + drift down-away
    p.style.transition = 'opacity 750ms ease-out, transform 750ms ease-out';
    p.style.opacity = '0';
    p.style.transform = 'translateY(26px)';
  }

  /**
   * The objective module's identity for this deploy: shown as the top-centre
   * label, and called out as a banner ("HOLD THE LINE — 1:30") on the next
   * show() so the player always knows WHAT they're doing before wave one.
   */
  setObjective(title: string, durationMs: number, countUp = false): void {
    this.objectiveTitle = title.toUpperCase();
    this.objectiveDurationMs = durationMs;
    this.objectiveCountUp = countUp;
    this.objLabel.textContent = this.objectiveTitle;
    // Endless count-up has no deadline bar — keep it filled as a steady "holding"
    // read; the timed objective bar starts empty and fills toward the clear.
    this.objFill.style.width = countUp ? '100%' : '0%';
    this.objFill.style.background = HUD_GREEN;
    this.pendingObjBanner = true;
  }

  /**
   * Per-frame: place the reticle at the gunner's aim point. `aim` is in view
   * space (0..1280, 0..720) — the SAME coordinates FiringSystem raycasts from —
   * so the crosshair, the bullets, and the hits all agree. Converted to CSS px
   * against the live parent size.
   */
  update(aim: Vec2, dt: number): void {
    const w = this.parent.clientWidth;
    const h = this.parent.clientHeight;
    const cx = (aim.x / this.viewW) * w;
    const cy = (aim.y / this.viewH) * h;
    if (this.fireBloom > 0) this.fireBloom = Math.max(0, this.fireBloom - dt / 90);
    const s = 1 + this.fireBloom * 0.5; // spread bloom on fire
    this.reticle.style.transform = `translate(${cx}px, ${cy}px) scale(${s})`;
    // Flare cooldown countdown → READY flip.
    if (this.flareCdLeftMs > 0) {
      this.flareCdLeftMs = Math.max(0, this.flareCdLeftMs - dt);
      this.paintFlares();
    }
  }

  /** Repaint the flare readiness line from the cooldown state. */
  private paintFlares(): void {
    if (this.flareCdLeftMs > 0) {
      this.flares.textContent = `FLARES ${Math.ceil(this.flareCdLeftMs / 1000)}s`;
      this.flares.style.color = HUD_GREEN;
      this.flares.style.opacity = '0.55';
    } else {
      this.flares.textContent = `FLARES READY${FLARE_HINT}`;
      this.flares.style.color = HUD_GREEN;
      this.flares.style.opacity = '1';
    }
  }

  /** Kick the reticle bloom on each shot (called from the weapon:fired event). */
  private readonly onFire = (): void => {
    this.fireBloom = 1;
  };

  /** Bind to a mission's event bus (called by App on mission start). */
  bind(bus: TypedEmitter, maxHull: number): void {
    this.unbind();
    this.bus = bus;
    this.maxHull = maxHull;
    this.setHull(maxHull, maxHull);
    this.scripTotal = 0;
    this.salvageTotal = 0;
    this.paintCurrency();
    this.flareCdLeftMs = 0;
    this.paintFlares();
    this.combo.textContent = '';
    this.objFill.style.width = '0%';

    bus.on('hull:damaged', this.onHull);
    bus.on('flare:deployed', this.onFlareDeployed);
    bus.on('enemy:firedProjectile', this.onEnemyProjectile);
    bus.on('projectile:decoyed', this.onDecoyed);
    bus.on('ammo:changed', this.onAmmo);
    bus.on('weapon:reloadStart', this.onReload);
    bus.on('weapon:switched', this.onWeapon);
    bus.on('friendly:hit', this.onFriendlyHit);
    bus.on('friendly:warning', this.onFriendlyWarning);
    bus.on('friendly:down', this.onFriendlyDown);
    bus.on('weapon:fired', this.onFire);
    bus.on('fx:hitmarker', this.onHitmarker);
    bus.on('scrip:changed', this.onScrip);
    bus.on('salvage:changed', this.onSalvage);
    bus.on('combo:changed', this.onCombo);
    bus.on('wave:banner', this.onWaveBanner);
    bus.on('objective:timer', this.onTimer);
    bus.on('mission:cleared', this.onCleared);
    bus.on('mission:failed', this.onFailed);
    bus.on('comms:line', this.onComms);
  }

  /** One crew radio line on the bottom ticker (urgent = warning colour). */
  private readonly onComms = (p: { text: string; speaker: string; urgent: boolean }): void => {
    if (this.commsTimeout) window.clearTimeout(this.commsTimeout);
    this.comms.textContent = `✦ ${p.speaker} ▸ ${p.text}`;
    this.comms.style.color = p.urgent ? HUD_WARN : HUD_GREEN;
    this.comms.style.opacity = '1';
    this.commsTimeout = window.setTimeout(() => {
      this.comms.style.opacity = '0';
      this.commsTimeout = 0;
    }, COMMS.holdMs);
  };

  /** Flares popped: start the cooldown readout (the per-frame update counts
   *  it down) — the burst itself is the AudioBus's thunk + sparkle. */
  private readonly onFlareDeployed = (p: { cooldownMs: number }): void => {
    this.flareCdLeftMs = p.cooldownMs;
    this.paintFlares();
  };

  /** A flare-able rocket is in the air — the big "press F NOW" moment. The
   *  banner holds about a rocket's flight so it clears itself either way;
   *  non-flareable projectiles (bullets, acid lobs) stay quiet. */
  private readonly onEnemyProjectile = (p: { flareable: boolean }): void => {
    if (!p.flareable) return;
    this.showBanner(`ROCKET INBOUND — FLARES${FLARE_HINT}`, HUD_WARN, 1300, 2);
  };

  /** The flare window caught the rocket — confirm the save loudly. */
  private readonly onDecoyed = (): void => {
    this.showBanner('ROCKET DECOYED', HUD_GREEN, 1100, 2);
  };

  private prevHull = Infinity;
  private readonly onHull = (p: { hull: number; maxHull: number }): void => {
    // Flash the red damage vignette when hull actually drops.
    if (p.hull < this.prevHull) {
      this.hurt.style.transition = 'none';
      this.hurt.style.boxShadow = 'inset 0 0 160px 50px rgba(255,30,20,0.55)';
      void this.hurt.offsetWidth;
      this.hurt.style.transition = 'box-shadow 380ms ease-out';
      this.hurt.style.boxShadow = 'inset 0 0 160px 40px rgba(255,30,20,0.0)';
    }
    this.prevHull = p.hull;
    this.setHull(p.hull, p.maxHull);
  };
  private readonly onAmmo = (p: { ammo: number; cap: number }): void => {
    this.lastAmmo = { ammo: p.ammo, cap: p.cap };
    this.paintAmmo();
  };
  private readonly onFriendlyHit = (): void => {
    // Hard red CEASE FIRE warning + a strong red vignette flash (per STRIKE —
    // the FiringSystem debounces a burst into one friendly:hit).
    this.showBanner('CEASE FIRE', HUD_WARN, 1000, 2);
    this.hurt.style.transition = 'none';
    this.hurt.style.boxShadow = 'inset 0 0 200px 60px rgba(255,40,30,0.6)';
    void this.hurt.offsetWidth;
    this.hurt.style.transition = 'box-shadow 500ms ease-out';
    this.hurt.style.boxShadow = 'inset 0 0 160px 40px rgba(255,30,20,0.0)';
  };
  /** Penultimate friendly-fire strike: ONE MORE fails the mission. The callout
   *  overrides the routine CEASE FIRE flash and holds longer (the klaxon in the
   *  audio layer fires off the same event). */
  private readonly onFriendlyWarning = (): void => {
    this.showBanner('CHECK FIRE — FINAL WARNING', HUD_WARN, 2200, 3);
    this.hurt.style.transition = 'none';
    this.hurt.style.boxShadow = 'inset 0 0 240px 80px rgba(255,40,30,0.75)';
    void this.hurt.offsetWidth;
    this.hurt.style.transition = 'box-shadow 700ms ease-out';
    this.hurt.style.boxShadow = 'inset 0 0 160px 40px rgba(255,30,20,0.0)';
  };
  /** A BLUEFOR friendly went down under the gun — a casualty line, not a frag. */
  private readonly onFriendlyDown = (): void => {
    this.showBanner('FRIENDLY DOWN', HUD_WARN, 1600, 2);
  };
  /** An authored wave beat ('CONTACT', 'BRUTE', 'THE SURGE') fired with its wave. */
  private readonly onWaveBanner = (p: { text: string }): void => {
    this.showBanner(p.text.toUpperCase(), HUD_RETICLE, 1800, 1);
  };
  private readonly onWeapon = (p: { weapon: WeaponId }): void => {
    // The live gun's code rides the ammo line (one line — no duplicate name
    // line, no decorative chrome echoing the same code).
    this.weaponCode = WEAPON_CODES[p.weapon] ?? (p.weapon as string).toUpperCase();
    this.paintAmmo();
  };
  /** Flash a pooled hitmarker at the impact point (view space → CSS px). */
  private readonly onHitmarker = (p: { at: Vec2; kill: boolean; headshot: boolean }): void => {
    const m = this.hitmarkers[this.hitmarkerIdx];
    this.hitmarkerIdx = (this.hitmarkerIdx + 1) % HITMARKER_POOL;
    if (!m) return;
    const cx = (p.at.x / this.viewW) * this.parent.clientWidth;
    const cy = (p.at.y / this.viewH) * this.parent.clientHeight;
    const base = p.headshot ? 1.35 : p.kill ? 1.15 : 0.9;
    m.style.color = p.kill ? HUD_RETICLE : '#ffffff';
    m.style.transition = 'none';
    m.style.opacity = '1';
    m.style.transform = `translate(${cx}px, ${cy}px) scale(${base})`;
    void m.offsetWidth; // reflow, then ease out
    m.style.transition = 'opacity 160ms ease-out, transform 160ms ease-out';
    m.style.opacity = '0';
    m.style.transform = `translate(${cx}px, ${cy}px) scale(${base * 1.45})`;
  };
  private readonly onReload = (): void => {
    this.ammo.textContent = `${this.weaponCode} · VENTING…`;
  };
  private readonly onScrip = (p: { total: number; delta: number }): void => {
    this.scripTotal = p.total;
    this.paintCurrency();
    if (p.delta) this.spawnPopup(p.delta);
  };
  private readonly onSalvage = (p: { total: number }): void => {
    this.salvageTotal = p.total;
    this.paintCurrency();
  };
  private readonly onCombo = (p: { mult: number; count: number }): void => {
    if (p.count <= 0) {
      this.combo.textContent = '';
      return;
    }
    // The combo is the PAY multiplier — say so, every time.
    this.combo.textContent = `${p.count} KILLS · ×${p.mult} PAY`;
    // Punch: a quick scale-pop on each combo tick (streak feedback).
    this.combo.style.transition = 'none';
    this.combo.style.transform = 'scale(1.35)';
    this.combo.style.color = p.mult >= 3 ? HUD_RETICLE : HUD_WARN;
    // Force reflow then ease back.
    void this.combo.offsetWidth;
    this.combo.style.transition = 'transform 180ms ease-out';
    this.combo.style.transform = 'scale(1)';
    // Milestone callout at high streaks.
    if (p.count > 0 && p.count % 15 === 0) {
      this.showBanner(`${p.count} KILL STREAK`, HUD_RETICLE, 1100, 1);
    }
  };
  private readonly onTimer = (p: { timeLeftMs: number; timeLimitMs: number }): void => {
    // ENDLESS: timeLeftMs is the time SURVIVED (counting up) and there is no
    // deadline — show it neutral, keep the "holding" bar full, no urgent flip.
    if (this.objectiveCountUp) {
      this.timer.textContent = this.fmt(p.timeLeftMs);
      this.timer.style.color = HUD_GREEN;
      return;
    }
    this.timer.textContent = this.fmt(p.timeLeftMs);
    const total = p.timeLimitMs || this.objectiveDurationMs;
    if (total > 0) {
      const done = Math.max(0, Math.min(1, 1 - p.timeLeftMs / total));
      this.objFill.style.width = `${(done * 100).toFixed(1)}%`;
    }
    // Final stretch: the whole objective module flips to warning amber.
    const urgent = p.timeLeftMs <= 15_000 && p.timeLeftMs > 0;
    this.timer.style.color = urgent ? HUD_RETICLE : HUD_GREEN;
    this.objFill.style.background = urgent ? HUD_RETICLE : HUD_GREEN;
  };
  // Mission verdicts hold until the HUD hides (top priority, no fade).
  private readonly onCleared = (): void => this.showBanner('AREA CLEAR', HUD_GREEN, 600_000, 4);
  private readonly onFailed = (p: { reason: string }): void =>
    this.showBanner(p.reason.toUpperCase(), HUD_WARN, 600_000, 4);

  private setHull(hull: number, maxHull: number): void {
    this.maxHull = maxHull;
    const pct = Math.max(0, Math.min(100, (hull / maxHull) * 100));
    this.hullText.textContent = `${Math.max(0, Math.ceil(hull))}`;
    this.hullFill.style.width = `${pct}%`;
    const color = pct <= 30 ? HUD_WARN : pct <= 60 ? HUD_RETICLE : HUD_GREEN;
    this.hullFill.style.background = color;
    this.hullText.style.color = color;
  }

  /** Show a centre callout. `holdMs` is how long it stays before fading;
   *  `prio` arbitrates collisions — while a banner holds, only an equal-or-
   *  higher priority one may replace it (a wave beat never stomps CHECK FIRE). */
  private showBanner(text: string, color: string, holdMs = 1200, prio = 0): void {
    const now = performance.now();
    if (now < this.bannerHoldUntil && prio < this.bannerPrio) return;
    if (this.bannerTimeout) window.clearTimeout(this.bannerTimeout);
    this.banner.textContent = text;
    this.banner.style.color = color;
    this.banner.style.opacity = '1';
    this.bannerPrio = prio;
    this.bannerHoldUntil = now + holdMs;
    this.bannerTimeout = window.setTimeout(() => {
      this.banner.style.opacity = '0';
      this.bannerTimeout = 0;
      this.bannerPrio = 0;
      this.bannerHoldUntil = 0;
    }, holdMs);
  }

  private fmt(ms: number): string {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  show(): void {
    this.root.style.display = 'block';
    // The deploy's one-shot objective callout — the player reads WHAT they're
    // doing the moment the HUD appears, before wave one lands.
    if (this.pendingObjBanner) {
      this.pendingObjBanner = false;
      const dur = this.objectiveDurationMs;
      const text = dur > 0 ? `${this.objectiveTitle} — ${this.fmt(dur)}` : this.objectiveTitle;
      this.showBanner(text, HUD_GREEN, 2800, 1);
    }
  }
  hide(): void {
    this.root.style.display = 'none';
    this.banner.style.opacity = '0';
  }

  unbind(): void {
    if (this.bannerTimeout) {
      window.clearTimeout(this.bannerTimeout);
      this.bannerTimeout = 0;
      this.bannerPrio = 0;
      this.bannerHoldUntil = 0;
    }
    if (this.commsTimeout) {
      window.clearTimeout(this.commsTimeout);
      this.commsTimeout = 0;
      this.comms.style.opacity = '0';
    }
    if (!this.bus) return;
    this.bus.off('hull:damaged', this.onHull);
    this.bus.off('flare:deployed', this.onFlareDeployed);
    this.bus.off('enemy:firedProjectile', this.onEnemyProjectile);
    this.bus.off('projectile:decoyed', this.onDecoyed);
    this.bus.off('ammo:changed', this.onAmmo);
    this.bus.off('weapon:reloadStart', this.onReload);
    this.bus.off('weapon:switched', this.onWeapon);
    this.bus.off('friendly:hit', this.onFriendlyHit);
    this.bus.off('friendly:warning', this.onFriendlyWarning);
    this.bus.off('friendly:down', this.onFriendlyDown);
    this.bus.off('weapon:fired', this.onFire);
    this.bus.off('fx:hitmarker', this.onHitmarker);
    this.bus.off('scrip:changed', this.onScrip);
    this.bus.off('salvage:changed', this.onSalvage);
    this.bus.off('combo:changed', this.onCombo);
    this.bus.off('wave:banner', this.onWaveBanner);
    this.bus.off('objective:timer', this.onTimer);
    this.bus.off('mission:cleared', this.onCleared);
    this.bus.off('mission:failed', this.onFailed);
    this.bus.off('comms:line', this.onComms);
    this.bus = null;
  }

  destroy(): void {
    this.unbind();
    this.root.remove();
  }
}
